import React, { useContext } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import {
  Card,
  Chip,
  IconButton,
  List,
  Text,
} from 'react-native-paper';

import { View } from 'native-base';
import Flag from 'react-native-flags';
import { formatDate } from '../../../utils/utils';
import { EVENT_PARTICIPANT_MODE } from '../../../utils/constant';
import { ISOCODE } from '../../../utils/isocode';

import MedalStats from '../../../components/commons/MedalStats';
import RankIcon from '../../../components/commons/RankIcon';
import { ModalContext } from '../../../context/ModalContext';

const reversedIsoCode = Object.entries(ISOCODE).reduce((acc, [code, name]) => {
  acc[name.toLowerCase()] = code;
  return acc;
}, {});

const getCountryCode = (location) => {
  if (!location) {
    return null;
  }
  const parts = location.split(',').map((p) => p.trim());
  const countryName = parts[parts.length - 1].toLowerCase();

  // Also check with the original case
  const countryNameOriginal = parts[parts.length - 1];

  return (
    reversedIsoCode[countryName]
    || Object.keys(ISOCODE).find((key) => ISOCODE[key] === countryNameOriginal)
  );
};

function TimelineItem({ event, achievements, isLast }) {
  const bestRank = Math.min(...achievements.map((a) => a.rank));
  const countryCode = getCountryCode(event.location);

  const renderTitle = () => (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      {countryCode && (
        <Flag code={countryCode} size={32} style={{ marginRight: 10 }} />
      )}
      <Text style={{ flex: 1 }}>{event.name}</Text>
    </View>
  );

  return (
    <View style={styles.timelineItem}>
      {!isLast && <View style={styles.timelineLine} />}
      <View style={styles.timelineIcon}>
        <View style={styles.timelineDot} />
      </View>
      <View style={styles.timelineContent}>
        <View
          style={{ justifyContent: 'space-between', flexDirection: 'row' }}
        >
          <Text style={styles.dateText}>{formatDate(event.startAt)}</Text>
          <Text style={styles.levelText}>{event.level}</Text>
        </View>
        {achievements.length === 1 ? (
          <List.Item
            style={styles.accordion}
            title={renderTitle}
            description={() => (
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Chip style={{ marginHorizontal: 1 }}>
                  {achievements[0].category}
                </Chip>
                {achievements[0].weightCategory && (
                  <Chip style={{ marginHorizontal: 1 }}>
                    {achievements[0].weightCategory}
                    {' '}
                    kg
                  </Chip>
                )}
                <Chip style={{ marginHorizontal: 1 }}>
                  {
                    Object.entries(EVENT_PARTICIPANT_MODE).find(
                      (item) => item[0] === achievements[0].mode,
                    )[1]
                  }
                </Chip>
              </View>
            )}
            left={(props) => <RankIcon rank={achievements[0].rank} {...props} />}
          />
        ) : (
          <List.Accordion
            style={styles.accordion}
            title={renderTitle}
            description="Appuyer pour voir les détails"
            left={(props) => <RankIcon rank={bestRank} {...props} />}
          >
            {achievements.map((achievement, index) => (
              <List.Item
                key={index}
                left={(props) => <RankIcon rank={achievement.rank} {...props} />}
                right={(props) => (
                  <View
                    style={[
                      props.style,
                      { flexDirection: 'row', alignItems: 'center' },
                    ]}
                  >
                    <Chip style={{ marginHorizontal: 1 }}>
                      {achievement.category}
                    </Chip>
                    {achievement.weightCategory && (
                      <Chip style={{ marginHorizontal: 1 }}>
                        {achievement.weightCategory}
                        {' '}
                        kg
                      </Chip>
                    )}
                    <Chip style={{ marginHorizontal: 1 }}>
                      {
                        Object.entries(EVENT_PARTICIPANT_MODE).find(
                          (item) => item[0] === achievement.mode,
                        )[1]
                      }
                    </Chip>
                  </View>
                )}
              />
            ))}
          </List.Accordion>
        )}
      </View>
    </View>
  );
}

function PalmaresTab({ user }) {
  const { toggleCreateAchievementModal } = useContext(ModalContext);

  const eventsWithAchievements = user?.events
    ?.filter((e) => e.achievements.length > 0)
    .sort((a, b) => new Date(b.event.startAt) - new Date(a.event.startAt));

  const stats = eventsWithAchievements?.reduce(
    (acc, { achievements }) => {
      achievements.forEach((achievement) => {
        if (achievement.rank === 1) {
          acc.gold += 1;
        } else if (achievement.rank === 2) {
          acc.silver += 1;
        } else if (achievement.rank === 3) {
          acc.bronze += 1;
        }
      });
      return acc;
    },
    { gold: 0, silver: 0, bronze: 0 },
  );

  const latestWeight = user?.profile?.weights?.[0]?.value;

  const handleAddAchievement = () => {
    toggleCreateAchievementModal({
      userId: user.id,
      gender: user?.profile?.gender,
      weight: latestWeight,
      events: user?.events,
    });
  };

  // Check if user has any ended events to add achievements to
  const hasEndedEvents = user?.events?.some(
    (e) => e.event.endAt && new Date(e.event.endAt) < new Date(),
  );

  if (!eventsWithAchievements || eventsWithAchievements.length === 0) {
    return (
      <Card.Content>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text>Aucun résultat pour le moment.</Text>
          {hasEndedEvents && (
            <IconButton icon="plus" mode="contained" onPress={handleAddAchievement} />
          )}
        </View>
      </Card.Content>
    );
  }

  return (
    <Card.Content style={{ flex: 1 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <MedalStats gold={stats.gold} silver={stats.silver} bronze={stats.bronze} />
        {hasEndedEvents && (
          <IconButton icon="plus" mode="contained" onPress={handleAddAchievement} />
        )}
      </View>
      <ScrollView>
        {eventsWithAchievements.map((item, index) => (
          <TimelineItem
            key={item.event.id}
            event={item.event}
            achievements={item.achievements}
            isLast={index === eventsWithAchievements.length - 1}
          />
        ))}
      </ScrollView>
    </Card.Content>
  );
}

const styles = StyleSheet.create({
  timelineItem: {
    flexDirection: 'row',
    position: 'relative', // for absolute positioning of the line
  },
  timelineIcon: {
    alignItems: 'center',
    width: 30,
    paddingTop: 15, // This will push the dot down
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: 'grey',
    zIndex: 1,
  },
  timelineLine: {
    position: 'absolute',
    top: 21, // paddingTop of timelineIcon + half of timelineDot height
    left: 14, // center of the icon container
    width: 2,
    height: '100%',
    backgroundColor: 'grey',
  },
  timelineContent: {
    flex: 1,
    paddingBottom: 20,
    paddingTop: 10, // 21 (center of dot) - 8 (half of dateText height)
  },
  accordion: {
    paddingVertical: 0,
  },
  dateText: {
    fontSize: 16,
    fontWeight: 'bold',
    // Removed marginBottom: 5, as paddingTop on timelineContent will handle spacing
  },
  levelText: {
    fontSize: 14,
    color: 'grey',
  },
  seasonTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 10,
  },
});

export default PalmaresTab;
